import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Layout from '@src/components/layout/Layout';
import CrewName from '@src/components/user/CrewName';
import {
  addDays,
  dayAndNumber,
  endOfMonth,
  endOfWeek,
  formatDate,
  getDaysBetween,
  isTheSameDay,
  startOfWeek,
} from '@src/utils/dateUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CrewButton from './components/CrewButton';
import useGetBookingAvailabilitiesLazy from '@src/adapters/hooks/availability/useGetBookingAvailabilities';
import UserBookingAvailabilityDTO from '@src/adapters/dto/user/UserBookingAvailabilityDTO';
import useFindBookingJobsLazy from '@src/adapters/hooks/job/useFindBookingJobs';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import useFindBookingListLazy from '@src/adapters/hooks/user/useFindBookingList';
import UserBookingDTO from '@src/adapters/dto/user/UserBookingDTO';
import useSaveBookingsLazy from '@src/adapters/hooks/job/useSaveBookings';
import { JobBookingStatus } from '@src/constants/JobBookingStatus';
import Filter from './components/Filter';

const PREFIX = 'Template';

const classes = {
  pageTitle: `${PREFIX}-pageTitle`,
  paper: `${PREFIX}-information`,
};

function Bookings() {
  const { execute: findCrewForBookingList } = useFindBookingListLazy();
  const { execute: findAvailabilityList } = useGetBookingAvailabilitiesLazy();
  const { execute: findAvailableJobs } = useFindBookingJobsLazy();
  const { execute: saveBookings } = useSaveBookingsLazy();

  const [crew, setCrew] = useState<UserBookingDTO[]>([]);
  const [jobs, setJobs] = useState<JobDTO[]>([]);

  const [assignments, setAssignments] = useState<
    { availabilityId: string; jobId: string }[]
  >([]);
  const [pencilConfirmIds, setPencilConfirmIds] = useState<string[]>([]);

  const [dateFrom, setDateFrom] = useState<Date>(startOfWeek());
  const [dateTo, setDateTo] = useState<Date>(endOfWeek());
  const [productionId, setProductionId] = useState<string>();
  const [jobId, setJobId] = useState<string>();

  const [availabilities, setAvailabilities] = useState<
    UserBookingAvailabilityDTO[]
  >([]);

  const findCrew = useCallback(() => {
    findCrewForBookingList({ dateFrom, dateTo, productionId, jobId }).then(
      (result) => {
        setCrew(result.users);
      }
    );
  }, [dateFrom, dateTo, productionId, jobId, findCrewForBookingList]);

  useEffect(() => {
    findCrew();
  }, [findCrew]);

  const findAvailabilities = useCallback(() => {
    findAvailabilityList({
      dateFrom,
      dateTo,
      productionId,
      jobId,
    }).then((result) => {
      setAvailabilities(result.availabilities);
    });
  }, [dateFrom, dateTo, productionId, jobId, findAvailabilityList]);

  useEffect(() => {
    findAvailabilities();
  }, [findAvailabilities]);

  const findJobs = useCallback(() => {
    findAvailableJobs({
      dateFrom,
      dateTo,
    }).then((result) => {
      setJobs(result.jobs);
    });
  }, [dateFrom, dateTo, findAvailableJobs]);

  useEffect(() => {
    findJobs();
  }, [findJobs]);

  const handleSaveClick = async () => {
    const response = await saveBookings({
      assignments,
      pencilConfirmIds,
    });

    setAvailabilities((prevAvailabilities) =>
      prevAvailabilities.map((a) => {
        const updated = response.updatedAvailabilities.find(
          (ua) => ua.id === a.id
        );
        return updated ? updated : a;
      })
    );
  };

  const days = useMemo(() => {
    return getDaysBetween(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  const handleAvailabilityClicked = async (
    availability: UserBookingAvailabilityDTO
  ) => {
    if (
      availability.jobStatus === JobBookingStatus.PencilAccepted &&
      availability.id
    ) {
      setPencilConfirmIds((prev) =>
        prev.includes(availability.id!)
          ? prev.filter((existingId) => existingId !== availability.id!)
          : [...prev, availability.id!]
      );
    }
  };

  const handleJobSelected = async (availabilityId: string, jobId: string) => {
    setAssignments((prevAssignments) => {
      const existingIndex = prevAssignments.findIndex(
        (assignment) => assignment.availabilityId === availabilityId
      );

      if (existingIndex !== -1) {
        const updatedAssignments = [...prevAssignments];
        updatedAssignments[existingIndex] = {
          ...updatedAssignments[existingIndex],
          jobId: jobId,
        };
        return updatedAssignments;
      } else {
        return [...prevAssignments, { availabilityId, jobId }];
      }
    });
  };

  const handleFilter = async (
    productionId: string | undefined,
    jobId: string | undefined,
    dateFrom: Date,
    dateTo: Date
  ) => {
    setDateFrom(dateFrom);
    setDateTo(dateTo);
    setProductionId(productionId);
    setJobId(jobId);
  };

  return (
    <Layout>
      <Box
        sx={{
          padding: '0 20px 20px 20px',
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        }}>
        <Typography variant="h4">Bookings</Typography>
        <Filter
          onFilter={handleFilter}
          jobs={
            productionId
              ? jobs.filter((j) => j.productionId === productionId)
              : []
          }
        />
        <Button
          onClick={handleSaveClick}
          color="secondary"
          variant="contained"
          size="medium">
          Save
        </Button>
      </Box>
      <Paper elevation={8} className={classes.paper}>
        <TableContainer
          style={{
            maxHeight: '60vh',
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            maxWidth: '90vw',
          }}>
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
            size="small"
            aria-label="Contents table"
            style={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  component="th"
                  align="left"
                  sx={{
                    width: '15%',
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    backgroundColor: '#151517',
                  }}>
                  Name
                </TableCell>
                <TableCell
                  component="th"
                  align="left"
                  sx={{ width: '15%', backgroundColor: '#151517' }}>
                  Diary Status
                </TableCell>
                <TableCell
                  component="th"
                  align="left"
                  sx={{ width: '15%', backgroundColor: '#151517' }}>
                  Updated
                </TableCell>
                <TableCell
                  component="th"
                  align="left"
                  sx={{ width: '15%', backgroundColor: '#151517' }}>
                  Disable Notification Until
                </TableCell>
                {days.map((d, index) => (
                  <TableCell
                    key={index}
                    component="th"
                    align="center"
                    sx={{
                      width: `${85 / days.length}%`,
                      backgroundColor: '#151517',
                    }}>
                    {dayAndNumber(d)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {crew.map((u, index) => (
                <TableRow key={index} sx={{ height: '60px' }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: '#151517',
                    }}>
                    <CrewName id={u.id} />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {u.disableNotificationUntil ? 'Off' : 'On'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {u.lastActivityUpdate &&
                      formatDate(u.lastActivityUpdate, 'DD/MM/YYYY HH:mm:ss')}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {u.disableNotificationUntil &&
                      formatDate(u.disableNotificationUntil, 'DD/MM/YYYY')}
                  </TableCell>
                  {days.map((d, index) => (
                    <TableCell component="th" align="center" key={index}>
                      <CrewButton
                        availability={availabilities.find(
                          (a) =>
                            isTheSameDay(new Date(a.date), d) &&
                            a.userId === u.id
                        )}
                        onAvailabilityClicked={handleAvailabilityClicked}
                        onJobSelected={handleJobSelected}
                        jobs={jobs}
                        roles={u.departmentRoleIds}
                        date={d}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Layout>
  );
}

export default Bookings;
